import { useEffect } from "react";

interface LotteryResult {
  time: string;
  number: string;
  special: string;
  third: string;
}

interface StructuredDataProps {
  title: string;
  description: string;
  results?: LotteryResult[] | undefined;
  country?: "nicaragua" | "costa-rica" | undefined;
  date?: string | undefined;
}

const StructuredData = ({ title, description, results, country, date }: StructuredDataProps) => {
  useEffect(() => {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create structured data for the page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "description": description,
      "url": window.location.href,
      "inLanguage": "es",
      "publisher": {
        "@type": "Organization",
        "name": "TiemposHoy.com",
        "url": "https://tiemposhoy.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://tiemposhoy.com/tiemposhoy-logo.png"
        }
      },
      "datePublished": date || new Date().toISOString(),
      "dateModified": new Date().toISOString()
    };

    // Add lottery result data if available
    if (results && results.length > 0 && country) {
      const lotteryName = country === "nicaragua" ? "Diaria Loto Nicaragua" : "Tiempos JPS Costa Rica";
      
      Object.assign(structuredData, {
        "@type": ["WebPage", "LotteryEvent"],
        "name": `${lotteryName} - ${title}`,
        "event": {
          "@type": "Event",
          "name": lotteryName,
          "description": description,
          "location": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": country === "nicaragua" ? "NI" : "CR"
            }
          },
          "startDate": date || new Date().toISOString()
        },
        "mainEntity": results.map(result => ({
          "@type": "Thing",
          "name": `Sorteo ${result.time}`,
          "description": `Resultado: ${result.number}${result.special ? ` - ${result.special}` : ''}${result.third ? ` - Final: ${result.third}` : ''}`
        }))
      });
    }

    // Insert the structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [title, description, results, country, date]);

  return null;
};

export default StructuredData;
